import {useState, useCallback} from 'react';
import {ROUTE} from '@/constants/main/route';
import useBackgroundLocation from '@/hooks/useBackgroundLocation';
import {useHelpContext} from '@/app/(main)/routes/help/context/HelpContext';
import styles from '@/components/help/pc/reportIllegal/reportIllegal.module.scss';
import {SoopButtonCommon} from '@soop-ui/soop-ui';
import InquiryNoticeBox from '@/components/help/common/InquiryNoticeBox';
import IllegalContentLayerPC from '@/components/help/pc/reportIllegal/IllegalContentLayerPC';
import useFileDownload from '@/hooks/help/useFileDownload';
import {FileDownloadType} from '@/hooks/help/useFileDownload';
import {useAutoCloseDialog} from '@/hooks/help';
import {getAppVersion, isWebview} from '@/utils/helper';
import {compareVersions} from 'compare-versions';

const ReportIllegalNotice = () => {
  const {isMobile} = useHelpContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const {downloadFile} = useFileDownload();
  const {navigateWithBackgroundLocation} = useBackgroundLocation();

  const handleExpandClick = () => {
    setIsExpanded(true);
  };
  const {openDialog} = useAutoCloseDialog();

  const navigateToMobileGuide = useCallback(() => {
    navigateWithBackgroundLocation(ROUTE.HELP.REPORT_ILLEGAL_GUIDE);
  }, [navigateWithBackgroundLocation]);

  const openPCGuideDialog = useCallback(() => {
    openDialog({
      displayType: 'center',
      scrollLock: true,
      component: () => <IllegalContentLayerPC />,
    });
  }, [openDialog]);

  const handleShowIllegalContentGuide = useCallback(() => {
    if (isMobile) {
      navigateToMobileGuide();
    } else {
      openPCGuideDialog();
    }
  }, [isMobile, navigateToMobileGuide, openPCGuideDialog]);

  const checkAppVersionAndDownload = useCallback(
    (type: FileDownloadType) => {
      const appVersion = getAppVersion() || '0.0.0';
      const requiredVersion = '8.15.0';

      if (compareVersions(appVersion, requiredVersion) < 0) {
        alert(
          '원활한 다운로드 기능 이용을 위하여\n최신 버전의 APP으로 업데이트해 주세요.',
        );
        return;
      }

      downloadFile(type);
    },
    [downloadFile],
  );

  const handleDownloadFile = useCallback(
    (type: FileDownloadType) => {
      if (isWebview) {
        checkAppVersionAndDownload(type);
        return;
      }

      downloadFile(type);
    },
    [checkAppVersionAndDownload, downloadFile],
  );

  return (
    <InquiryNoticeBox
      className={!isExpanded ? styles.hidden : ''}
      title='신고 전 확인해보세요!'
      list={[
        {
          text: '증거자료가 없을 경우 신고가 불가능 합니다.',
        },
        {
          text: '운영정책의 위반 정도에 따라 경고부터 이용정지까지 처리 될 수 있으며, 음란물 및 운영정책 위반 정도가 심할 경우 경고 없이 바로 이용정지 될 수 있습니다.',
        },
        {
          text: '타인을 비방하거나 고의적인 허위 신고의 경우 신고자 또한 제재를 받을 수 있습니다.',
        },
      ]}
      bottomSlot={
        isMobile &&
        !isExpanded && (
          <div className={styles.ShowButtonArea}>
            <SoopButtonCommon
              endIconName='icoChevronDown'
              onClick={handleExpandClick}
              size={32}
              variant='defaultSecondary'>
              더보기
            </SoopButtonCommon>
          </div>
        )
      }>
      <div className={styles.IllegalSection}>
        <p className={styles.desc}>
          불법촬영물등에 대한 신고 및 삭제요청은 전기통신사업법 제22조의5 제1항
          및 동법 시행령 제30조의5 제2항에 따라, 아래의 링크에서
          &quot;불법촬영물등 유통 신고&middot;삭제요청서&quot;를 다운로드 받아
          여기에 작성하여 제출하시거나, 같은 서식의 내용이 포함된 아래의
          입력란에 직접 기입하여 접수시키실 수 있습니다.
        </p>
        <button
          type='button'
          className={styles.ContentBtn}
          onClick={handleShowIllegalContentGuide}>
          불법촬영물등 유형과 정의 안내
        </button>
        <div className={styles.DownloadArea}>
          <span>불법촬영물등 유통 신고·삭제요청서 다운받기</span>
          <div className={styles.BtnArea}>
            <SoopButtonCommon
              onClick={() => handleDownloadFile('hwp')}
              size={isMobile ? 40 : 32}
              variant='lineSecondary'>
              한글파일
            </SoopButtonCommon>
            <SoopButtonCommon
              onClick={() => handleDownloadFile('docx')}
              size={isMobile ? 40 : 32}
              variant='lineSecondary'>
              워드파일
            </SoopButtonCommon>
          </div>
        </div>
        <div className={styles.NoticeMessage}>
          <strong>유의사항</strong>
          <p>
            신고·삭제요청(서) 각각의 항목 중 기재되지 않은 항목이 있거나, 대상이
            된 불법촬영물등이 특정되지 않아 검토가 어려운 경우, 삭제 등의 조치가
            취해지지 못하고 신고·삭제요청하신 내용에 대해 보완을 요청 드릴 수
            있습니다.
          </p>
        </div>
      </div>
    </InquiryNoticeBox>
  );
};

export default ReportIllegalNotice;
